import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { VISITOR_LOCALE_INFO, t as sharedT } from '@cloudsign/shared';
import type { VisitorLocale } from '@cloudsign/shared';
import type { LinkLanguages } from '../public-links/api';

/**
 * Visitor language on the public pages (docs/modules/languages.md s3): the picker, the remembered choice (local storage,
 * per tenant) and right-to-left layout for Arabic. Only visitor-prefixed keys are translated; `vt` falls back to English
 * key by key, so a missing translation never breaks a page.
 */

const STORE = 'cloudsign.visitorLanguage.';

function readStored(scope: string): string | null {
  try {
    return window.localStorage.getItem(STORE + scope);
  } catch {
    return null;
  }
}

export interface VisitorLanguage {
  locale: VisitorLocale;
  dir: 'ltr' | 'rtl';
  offered: LinkLanguages['offered'];
  setLocale: (l: VisitorLocale) => void;
  /** Translate a visitor key into the chosen language. */
  vt: (key: string, params?: Record<string, string | number>) => string;
}

const FALLBACK: VisitorLanguage = {
  locale: 'en-GB',
  dir: 'ltr',
  offered: [],
  setLocale: () => undefined,
  vt: (key, params) => sharedT(key, params),
};
const Ctx = createContext<VisitorLanguage>(FALLBACK);

/** The language of the nearest public page. English with no picker when used outside one. */
export const useVisitorLanguage = (): VisitorLanguage => useContext(Ctx);

/**
 * Chooses the language: the remembered one when it is still on offer, else the server's default, else English.
 * `scope` is the tenant (slug) so a choice on one organisation's page does not leak into another's.
 */
export function VisitorLanguageProvider({ scope, languages, children }: { scope: string; languages: LinkLanguages | undefined; children: ReactNode }) {
  const [picked, setPicked] = useState<string | null>(() => readStored(scope));
  const offered = languages?.offered ?? [];
  const locale: VisitorLocale = useMemo(() => {
    const ok = (c: string | null): c is VisitorLocale => c !== null && offered.some((o) => o.code === c);
    if (ok(picked)) return picked;
    return languages && (offered.length === 0 || ok(languages.default)) ? languages.default : 'en-GB';
  }, [picked, offered, languages]);
  const setLocale = useCallback(
    (l: VisitorLocale) => {
      setPicked(l);
      try {
        window.localStorage.setItem(STORE + scope, l);
      } catch {
        /* private browsing: the choice just is not remembered */
      }
    },
    [scope],
  );
  const value = useMemo<VisitorLanguage>(
    () => ({ locale, dir: VISITOR_LOCALE_INFO[locale].dir, offered, setLocale, vt: (key, params) => sharedT(key, params, locale) }),
    [locale, offered, setLocale],
  );
  return (
    <Ctx.Provider value={value}>
      <div lang={locale} dir={value.dir}>
        {children}
      </div>
    </Ctx.Provider>
  );
}

/** A labelled drop-down of the languages on offer, each shown in its own language. Hidden when there is only one. */
export function LanguagePicker() {
  const { locale, offered, setLocale, vt } = useVisitorLanguage();
  if (offered.length < 2) return null;
  return (
    <div className="ms-auto flex items-center gap-2">
      <label htmlFor="visitor-language" className="text-sm text-slate-700">
        {vt('public.language.label')}
      </label>
      <select
        id="visitor-language"
        value={locale}
        onChange={(e) => setLocale(e.target.value as VisitorLocale)}
        className="min-h-[2.5rem] rounded-md border border-slate-300 bg-white px-2 text-sm"
      >
        {offered.map((o) => (
          <option key={o.code} value={o.code} lang={o.code} dir={o.dir}>
            {o.endonym}
          </option>
        ))}
      </select>
    </div>
  );
}
